import { useCallback } from 'react';
import { Resource } from '@setvi/shared/interfaces';

import { useSubscribedMutation } from 'Hooks/React-Query';

import type { PrintOrderItem, PrintOrderItemCustomization } from '../types';
import { usePrintOrderStore, createPrintOrderItem } from '../store';
import {
  syncShoppingCartMutation,
  deleteShoppingCartMutation,
  checkoutShoppingCartMutation
} from '../api/shopping-cart';

/**
 * Cart operations backed by React Query mutations. Every ShoppingCart API call
 * (create/update + delete) runs through a mutation here; the zustand store only
 * holds the resulting state. Components read the loading flags (`isSyncing`,
 * `isDeleting`, `isCheckingOut`) from this hook instead of tracking their own.
 * Error toasts are handled globally by the mutation cache (see `useReactQuery`),
 * so callers only toast on success.
 */
export const usePrintOrderCart = () => {
  const applySync = usePrintOrderStore(state => state.applySync);
  const resetCart = usePrintOrderStore(state => state.resetCart);
  // Cart mutations in flight anywhere in the app (each hook instance has its
  // own React Query mutation, so local isLoading flags can't see each other).
  const isBusy = usePrintOrderStore(state => state.pendingMutations > 0);

  const { mutateAsync: syncCart, isLoading: isSyncing } = useSubscribedMutation(
    syncShoppingCartMutation()
  );
  const { mutateAsync: deleteCart, isLoading: isDeleting } =
    useSubscribedMutation(deleteShoppingCartMutation());
  const { mutateAsync: checkoutCart, isLoading: isCheckingOut } =
    useSubscribedMutation(checkoutShoppingCartMutation());

  // Run one server cart mutation under the store's global in-flight counter,
  // so every other Add to Cart / cart action is disabled while it runs.
  const track = useCallback(async <T>(op: () => Promise<T>): Promise<T> => {
    const { beginMutation, endMutation } = usePrintOrderStore.getState();
    beginMutation();
    try {
      return await op();
    } finally {
      endMutation();
    }
  }, []);

  // Push the next items to the server, then commit them locally on success.
  const commit = useCallback(
    (nextItems: PrintOrderItem[]) =>
      track(async () => {
        const { sessionId } = usePrintOrderStore.getState();
        const data = await syncCart({ sessionId, items: nextItems });
        applySync(nextItems, data);
      }),
    [track, syncCart, applySync]
  );

  const addItem = useCallback(
    async (resource: Resource, customization?: PrintOrderItemCustomization) => {
      const { items } = usePrintOrderStore.getState();
      if (items.some(i => i.resource.ID === resource.ID)) return;
      await commit([
        ...items,
        { ...createPrintOrderItem(resource), ...customization }
      ]);
    },
    [commit]
  );

  // Edit an existing item (QR/cover customization) and persist to the server.
  const updateItem = useCallback(
    async (id: number, updates: PrintOrderItemCustomization) => {
      const { items } = usePrintOrderStore.getState();
      if (!items.some(i => i.resource.ID === id)) return;
      const nextItems = items.map(i =>
        i.resource.ID === id ? { ...i, ...updates } : i
      );
      await commit(nextItems);
    },
    [commit]
  );

  const removeItem = useCallback(
    async (id: number) => {
      const { items, cartId } = usePrintOrderStore.getState();
      const nextItems = items.filter(i => i.resource.ID !== id);
      if (nextItems.length === items.length) return;
      if (!nextItems.length) {
        // Removing the last item deletes the whole server cart.
        await track(async () => {
          if (cartId != null) await deleteCart(cartId);
          resetCart();
        });
        return;
      }
      await commit(nextItems);
    },
    [commit, deleteCart, resetCart, track]
  );

  // Cancel: delete the server cart first (so the action bar can show a loading
  // state), then clear local state. Rejects if the delete fails, leaving the
  // cart intact; the mutation cache surfaces the error toast.
  const clearCart = useCallback(
    () =>
      track(async () => {
        const { cartId } = usePrintOrderStore.getState();
        if (cartId != null) await deleteCart(cartId);
        resetCart();
      }),
    [track, deleteCart, resetCart]
  );

  // Checkout: submit the cart for print, clear it locally, and return the
  // checkout result (carrying the external RedirectUrl to open).
  const checkout = useCallback(
    () =>
      track(async () => {
        const { cartId } = usePrintOrderStore.getState();
        if (cartId == null) return null;
        const result = await checkoutCart(cartId);
        resetCart();
        return result;
      }),
    [track, checkoutCart, resetCart]
  );

  return {
    addItem,
    removeItem,
    updateItem,
    clearCart,
    checkout,
    // Persist a full set of items to the server (used to save a preselected
    // cart without a per-item merge).
    saveItems: commit,
    // Global-aware: true while ANY cart mutation is in flight (this instance's
    // or another's), so concurrent Add to Cart / save actions stay disabled.
    isSyncing: isSyncing || isBusy,
    isDeleting,
    isCheckingOut,
    // Combined busy flag (used by the resource dropdown's add action).
    isLoading: isSyncing || isDeleting || isBusy
  };
};
